
// ReportTemplateCollatedDirect.js
// Multi-chart PDF generator with capture → PNGs → PDF blob → parent preview modal
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BlobProvider,
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import DSILogo from "../../assets/DST-logo.jpg";
import SAASTALogo from "../../assets/saasta-logo.png";
import { GenericChart } from "./GenericChart";
import { GenericGeoMap } from "./GenericGeoMap";
import { reportTypeEnum } from "./AppEnums";

// -------------------------------------------------------
// FONT
// -------------------------------------------------------
Font.register({
  family: "Roboto",
  src: "https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf",
});

// -------------------------------------------------------
// DOM CAPTURE FALLBACK (charts primarily push PNG via onImageSrc)
// -------------------------------------------------------
const ChartCapture = ({
  width = 1100,
  height = 600,
  wait = 800,
  pollEvery = 300,
  maxWait = 15000,
  onCaptured,
  children,
}) => {
  const boxRef = useRef(null);
  const doneRef = useRef(false);

  const svgToPng = async (svgEl, w, h) => {
    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(svgEl);
    if (!svgString.startsWith("<?xml")) {
      svgString = `<?xml version="1.0" standalone="no"?>\n${svgString}`;
    }
    const svgDataUrl =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = svgDataUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/png");
  };

  const captureNow = async () => {
    if (doneRef.current) return false;
    const host = boxRef.current;
    if (!host) return false;

    // Try CANVAS
    const canvas = host.querySelector("canvas");
    if (canvas && canvas.toDataURL) {
      try {
        const png = canvas.toDataURL("image/png");
        if (png.startsWith("data:image/png")) {
          doneRef.current = true;
          onCaptured?.(png);
          return true;
        }
      } catch (e) {
        console.warn("ChartCapture: canvas toDataURL failed", e);
      }
    }

    // Try SVG
    const svg = host.querySelector("svg");
    if (svg) {
      try {
        const r = host.getBoundingClientRect();
        const w = Math.max(r.width, width);
        const h = Math.max(r.height, height);
        const png = await svgToPng(svg, w, h);
        if (png.startsWith("data:image/png")) {
          doneRef.current = true;
          onCaptured?.(png);
          return true;
        }
      } catch (e) {
        console.warn("ChartCapture: svg export failed", e);
      }
    }
    return false;
  };

  useEffect(() => {
    let mounted = true;
    const start = setTimeout(async () => {
      if (!mounted) return;
      if (await captureNow()) return;
      const poll = setInterval(async () => {
        if (!mounted || doneRef.current) return clearInterval(poll);
        if (await captureNow()) clearInterval(poll);
      }, pollEvery);
      return () => clearInterval(poll);
    }, wait);

    const deadline = setTimeout(() => captureNow(), maxWait);
    return () => {
      mounted = false;
      clearTimeout(start);
      clearTimeout(deadline);
    };
  }, [wait, pollEvery, maxWait]);

  return (
    <div
      ref={boxRef}
      style={{
        width,
        height,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
};

// -------------------------------------------------------
// MAIN
// -------------------------------------------------------
export const ReportTemplateCollatedDirect = ({
  reportOutProps,
  onProgress,
  onReady,
  onBlobReady, // (url, blob)
}) => {
  const charts = reportOutProps?.charts ?? [];
  const isMulti = !!reportOutProps?.isMulti;
  const runId = reportOutProps?.runId;

  const [multiImages, setMultiImages] = useState({});
  const capturedCount = Object.keys(multiImages).length;
  const expectedCount = reportOutProps?.expectedCount ?? charts.length;
  const allCaptured = capturedCount >= expectedCount && expectedCount > 0;

  // Prevent double-emission of the blob and late progress
  const emitted = useRef(false);

  // Per-index accept-once guard to ignore duplicates/late arrivals
  const acceptedIdxRef = useRef(new Set());

  // Fresh session key for chart element keys
  const [sessionKey] = useState(() => Date.now());

  // ✅ Hard reset on a new run (ensures clean 2nd+ runs)
  useEffect(() => {
    setMultiImages({});
    emitted.current = false;
    acceptedIdxRef.current = new Set();
  }, [runId]);

  // ---------------------------------------------
  // PROGRESS (only while still capturing)
  // ---------------------------------------------
  useEffect(() => {
    if (emitted.current) return;
    if (capturedCount > expectedCount) return;
    onProgress?.(capturedCount, expectedCount);
  }, [capturedCount, expectedCount, onProgress]);

  // Final guaranteed 100% tick (guarded)
  useEffect(() => {
    if (!allCaptured) return;
    if (emitted.current) return;
    onProgress?.(expectedCount, expectedCount);
  }, [allCaptured, expectedCount, onProgress]);

  // Notify ready with a small grace delay so 100% is visible (guarded)
  useEffect(() => {
    if (!allCaptured) return;
    if (emitted.current) return;
    const t = setTimeout(() => onReady?.(), 300);
    return () => clearTimeout(t);
  }, [allCaptured, onReady]);

  // ---------------------------------------------
  // Styles
  // ---------------------------------------------
  const styles = StyleSheet.create({
    coverMetaTable: {
      width: "100%",
      borderWidth: 1,
      borderColor: "#DADDE2",
      marginTop: 18,
      borderRadius: 10,
      overflow: "hidden",
      // shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.14,
      shadowRadius: 4,
      elevation: 3,
    },
    coverMetaHeader: {
      flexDirection: "row",
      paddingVertical: 14,
      paddingHorizontal: 12,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#D3D3D3",
      // If gradients are supported in your renderer, keep the gradient.
      // Otherwise, switch to a solid brand color like "#004b87".
      // backgroundColor: "linear-gradient(90deg, #004b87, #3fa9f5)",
    },
    coverMetaHeaderText: {
      width: "100%",
      textAlign: "center",
      color: "#000",
      fontSize: 16,             // increased size
      fontWeight: "800",        // stronger bold
      letterSpacing: 0.6,       // gives a premium feel
      textTransform: "uppercase", // makes it stand out
      paddingVertical: 4,
    },
    // Rows
    coverMetaRow: {
      flexDirection: "row",
      paddingVertical: 9,
      paddingHorizontal: 12,
      backgroundColor: "#E5E5E5",     // light grey background
    },

    coverMetaRowAlt: {
      flexDirection: "row",
      paddingVertical: 9,
      paddingHorizontal: 12,
      backgroundColor: "#DADADA",     // slightly darker grey for alternating rows
    },
    // Columns
    coverMetaCellTitle: {
      width: "40%",
      fontSize: 12,
      fontWeight: "bold",
      color: "#000000",
      textAlign: "left",              // << LEFT ALIGNED
      paddingRight: 6,
    },
    coverMetaCellValue: {
      width: "60%",
      fontSize: 12,
      color: "#000000",
      textAlign: "left",
      lineHeight: 1.3,
      paddingLeft: 6,
    },

    page: {
      flexDirection: "column",
      alignItems: "center",
      padding: "1cm",
      fontFamily: "Roboto",
    },
    header: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 20,
    },
    logo: { width: 120, height: 50 },
    title: {
      textAlign: "center",
      fontSize: 20,
      marginBottom: 20,
      fontWeight: "bold",
    },
    image: { width: "100%", maxWidth: "1200px" },
    footer: {
      position: "absolute",
      bottom: 20,
      textAlign: "center",
      color: "grey",
      fontSize: 12,
    },
    table: {
      marginTop: 20,
      marginBottom: 20,
      fontSize: 12,
      width: "100%",
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#efefef",
      padding: 4,
      borderBottomWidth: 1,
      borderBottomColor: "#ccc",
    },
    tableRow: {
      flexDirection: "row",
      padding: 4,
      borderBottomWidth: 1,
      borderBottomColor: "#ddd",
    },
    col1: { width: "50%" },
    col2: { width: "25%" },
    col3: { width: "25%" },

    // Cover page styles
    coverPage: {
      flexDirection: "column",
      padding: "1.5cm",
      fontFamily: "Roboto",
    },
    coverHeader: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 24,
    },
    coverLogo: { width: 140, height: 58 },
    coverTitle: {
      fontSize: 22,
      textAlign: "center",
      fontWeight: "bold",
      marginBottom: 10,
    },
    coverSubtitle: {
      fontSize: 14,
      textAlign: "center",
      marginBottom: 20,
    },
    metaBlock: {
      width: "100%",
      borderWidth: 1,
      borderColor: "#e0e0e0",
      padding: 12,
      borderRadius: 4,
      marginBottom: 18,
    },
    metaRow: {
      fontSize: 12,
      marginBottom: 6,
    },
    coverFooter: {
      position: "absolute",
      bottom: 20,
      left: 0,
      right: 0,
      textAlign: "center",
      color: "grey",
      fontSize: 12,
    },

    footer: {
      position: "absolute",
      bottom: 20,
      textAlign: "center",
      color: "grey",
      fontSize: 12,
    },
    pageNumber: {
      position: "absolute",
      bottom: 8,
      right: 20,
      fontSize: 12,
      color: "grey",
    },

    coverFooter: {
      position: "absolute",
      bottom: 20,
      left: 0,
      right: 0,
      textAlign: "center",
      color: "grey",
      fontSize: 12,
    },
  });

  const renderTable = (chart) => {
    if (!Array.isArray(chart.data)) return null;
    const rows = chart.data;
    const total = rows.reduce((a, c) => a + (Number(c?.value) || 0), 0);
    return (
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.col1}>Measure</Text>
          <Text style={styles.col2}>Value</Text>
          <Text style={styles.col3}>Percent (%)</Text>
        </View>
        {rows.map((row, i) => (
          <View key={i} style={styles.tableRow}>
            <Text style={styles.col1}>{row?.type}</Text>
            <Text style={styles.col2}>{row?.value}</Text>
            <Text style={styles.col3}>
              {total > 0
                ? (((Number(row?.value) || 0) / total) * 100).toFixed(2)
                : "0.00"}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  // Accept a PNG for index i exactly once
  const acceptPngOnce = (i, png) => {
    if (!png || typeof png !== "string") return;
    if (acceptedIdxRef.current.has(i)) return;
    setMultiImages((prev) => {
      if (prev[i]) return prev; // already set
      acceptedIdxRef.current.add(i);
      return { ...prev, [i]: png };
    });
  };

  // ---------------------------------------------
  // CHART rendering (prefer onImageSrc; DOM capture as fallback)
  // ---------------------------------------------
  const renderChartForIndex = (chart, i) => {
    if (
      chart.type === reportTypeEnum.GEO_PROVINCES ||
      chart.type === reportTypeEnum.GEO_MUNICIPALS ||
      chart.type === reportTypeEnum.GEO_LOCAL_MUNICIPALITIES
    ) {
      return (
        <GenericGeoMap
          key={`gm-${sessionKey}-${i}`}
          data={chart.data}
          dataSource={chart.dataSource}
          hoverText={chart.description}
          rptType={chart.type}
          width="100%"
          annotatedCssContainer="census-info-hover__preview"
          annotatedCssText="census-info__preview"
          onImageSrc={(png) => acceptPngOnce(i, png)}
          onLegendSrc={() => { }}
        />
      );
    }

    return (
      <GenericChart
        key={`chart-${sessionKey}-${i}`}
        data={(Array.isArray(chart.data) ? chart.data : []).filter((d) =>
          typeof d?.value === "number" ? d.value > 0 : true
        )}
        type={chart.type}
        color={chart.color}
        year={reportOutProps.year}
        description={chart.description}
        title={chart.title}
        xLabel={chart.xLabel}
        yLabel={chart.yLabel}
        xField={chart.xField}
        yField={chart.yField}
        onImageSrc={(png) => acceptPngOnce(i, png)}
        onLegendSrc={() => { }}
      />
    );
  };

  const CoverMeta = ({ meta }) => {
    if (!Array.isArray(meta) || meta.length === 0) return null;
    return (
      <View style={styles.metaBlock}>
        {meta.map((m, i) => (
          <Text key={i} style={styles.metaRow}>
            {m.title}: {m.value}
          </Text>
        ))}
      </View>
    );
  };

  // ---------------------------------------------
  // Build PDF document
  // ---------------------------------------------
  // Format a JS Date into "DD-MMM-YYYY" (e.g., "27-Feb-2026")
  const formatFootnoteDate = (dt = new Date()) => {
    const dd = String(dt.getDate()).padStart(2, "0");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mmm = months[dt.getMonth()];
    const yyyy = dt.getFullYear();
    return `${dd}-${mmm}-${yyyy}`;
  };

  // Format a year like 2025 into "2025/2026" for the cover page
  const fyLabel = useMemo(() => {
    const y = Number(reportOutProps?.year);
    return Number.isFinite(y) ? `${y}/${y + 1}` : (reportOutProps?.year ?? "");
  }, [reportOutProps?.year]);

  const PdfDoc = useMemo(
    () => (
      <Document pageMode="fullScreen">
        {/* ---- COVER PAGE (only if eventMeta is present) ---- */}
        {reportOutProps?.reportKind && (
          <Page style={styles.coverPage}>
            <View style={styles.coverHeader}>
              <Image style={styles.coverLogo} src={DSILogo} />
              <Image style={styles.coverLogo} src={SAASTALogo} />
            </View>

            <Text style={styles.coverTitle}>
              {reportOutProps.reportKind || "Events Report"}
            </Text>
            {/* <Text style={styles.coverSubtitle}>
              Financial Year: {fyLabel}
            </Text> */}

            <View style={styles.coverMetaTable}>

              {/* HEADER ROW */}
              <View style={styles.coverMetaHeader}>
                <Text style={styles.coverMetaHeaderText}>
                  Event Details
                </Text>
              </View>

              {/* DATA ROWS */}
              {reportOutProps.eventMeta?.map((m, i) => (
                <View
                  key={i}
                  style={i % 2 === 0 ? styles.coverMetaRow : styles.coverMetaRowAlt}
                >
                  <Text style={styles.coverMetaCellTitle}>{m.title}</Text>
                  <Text style={styles.coverMetaCellValue}>{m.value}</Text>
                </View>
              ))}

            </View>

            {/* (Optional) You can add a short description or disclaimer here */}

            <Text style={styles.coverFooter}>
              Report produced by SEIMS System on{" "}
              {formatFootnoteDate()}
            </Text>

            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
              fixed
            />

          </Page>
        )}

        {/* ---- CHART PAGES ---- */}
        {charts.map((chart, idx) => (
          <Page key={idx} style={styles.page}>
            <View style={styles.header}>
              <Image style={styles.logo} src={DSILogo} />
              <Image style={styles.logo} src={SAASTALogo} />
            </View>

            <Text style={styles.title}>
              {chart.title}
            </Text>

            {multiImages[idx] ? (
              <Image style={styles.image} src={multiImages[idx]} />
            ) : (
              <Text>Preparing image…</Text>
            )}

            {renderTable(chart)}

            <Text style={styles.footer}>
              Report produced by SEIMS System on{" "}
              {formatFootnoteDate()}
            </Text>

            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
              fixed
            />

          </Page>
        ))}
      </Document>
    ),
    [charts, multiImages, reportOutProps.year, reportOutProps.eventMeta, reportOutProps.reportKind]
  );

  // ---------------------------------------------
  // RENDER: HIDDEN CAPTURE + BLOB PROVIDER
  // ---------------------------------------------
  return (
    <>
      {/* Hidden capture area — render only while capturing */}
      {isMulti && !allCaptured && !emitted.current && (
        <div
          key={`cap-wrap-${runId}`}
          style={{
            position: "absolute",
            top: "200vh",
            left: 0,
            opacity: 0.01,
            pointerEvents: "none",
            width: 1400,
          }}
        >
          {charts.map((chart, idx) => (
            <ChartCapture
              key={`cap-${sessionKey}-${idx}`}
              width={1100}
              height={600}
              wait={800}
              pollEvery={300}
              maxWait={15000}
              onCaptured={(png) => acceptPngOnce(idx, png)}
            >
              {renderChartForIndex(chart, idx)}
            </ChartCapture>
          ))}
        </div>
      )}

      {/* Produce blob, send to parent exactly once */}
      {isMulti && allCaptured && (
        <BlobProvider document={PdfDoc}>
          {({ url, loading, error, blob }) => {
            if (!loading && url && !error && !emitted.current) {
              emitted.current = true;
              onBlobReady?.(url, blob);
            }
            return null;
          }}
        </BlobProvider>
      )}
    </>
  );
};
